import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useUsers } from '../context/usersContext';

const normalizeGroupName = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/\s+/g, ' ')
  .trim()
  .toUpperCase();

// Perfis externos somente-leitura (acesso restrito a /internos + tela de detalhes externa).
// Ao liberar um novo perfil externo com esse mesmo comportamento, acrescentar o grupo aqui.
const EXTERNAL_PROFILE_GROUPS = ['TJPI', 'POLÍCIA MILITAR / POLÍCIA CIVIL', 'DEFENSORIA', 'MPPI'];

// Sentinela usada em routeAccessGroups para liberar uma rota a qualquer usuário autenticado,
// independente do grupo (ex: /usuario, que é a própria página de dados/senha do usuário).
const ALL_GROUPS_SENTINEL = '*';

const routeAccessGroups = {
  // Rotas para ADVOGADO(A)
  '/advogado': ['ADVOGADO(A)'],

  // Rotas gerais para todos exceto advogado
  '/': ['DIRETOR', 'ADMINISTRATIVO', 'PLANTONISTA', 'DISCIPLINA', 'SERVIÇO SOCIAL', 'PSICOLOGIA', 'DENTISTA', 'NUTRIÇÃO', 'ENFERMAGEM', 'MEDICINA', 'EDUCAÇÃO', 'GERENTE DE UNIDADE', 'DIRETORES', 'DUAP', 'MONITORAMENTO'],
  '/quantitativo': ['DIRETOR', 'ADMINISTRATIVO', 'PLANTONISTA', 'DISCIPLINA', 'SERVIÇO SOCIAL', 'PSICOLOGIA', 'DENTISTA', 'NUTRIÇÃO', 'ENFERMAGEM', 'MEDICINA', 'EDUCAÇÃO', 'GERENTE DE UNIDADE', 'DIRETORES', 'DUAP', 'MONITORAMENTO'],
  '/pesquisar-prontuario': ['DIRETOR', 'ADMINISTRATIVO', 'PLANTONISTA', 'DISCIPLINA', 'SERVIÇO SOCIAL', 'PSICOLOGIA', 'DENTISTA', 'NUTRIÇÃO', 'ENFERMAGEM', 'MEDICINA', 'EDUCAÇÃO', 'GERENTE DE UNIDADE', 'DIRETORES', 'DUAP', 'MONITORAMENTO'],
  // Defensoria (perfil externo) também acessa Relatórios — ver Sidebar/index.js (isDefensoria).
  '/relatorios': ['DIRETOR', 'ADMINISTRATIVO', 'PLANTONISTA', 'DISCIPLINA', 'SERVIÇO SOCIAL', 'PSICOLOGIA', 'DENTISTA', 'NUTRIÇÃO', 'ENFERMAGEM', 'MEDICINA', 'EDUCAÇÃO', 'GERENTE DE UNIDADE', 'DIRETORES', 'DUAP', 'MONITORAMENTO', 'DEFENSORIA'],
  '/treinamentos': ['DIRETOR', 'ADMINISTRATIVO', 'PLANTONISTA', 'DISCIPLINA', 'SERVIÇO SOCIAL', 'PSICOLOGIA', 'DENTISTA', 'NUTRIÇÃO', 'ENFERMAGEM', 'MEDICINA', 'EDUCAÇÃO', 'GERENTE DE UNIDADE', 'DIRETORES', 'DUAP', 'MONITORAMENTO'],
  '/paineis': ['DIRETOR', 'ADMINISTRATIVO', 'PLANTONISTA', 'DISCIPLINA', 'SERVIÇO SOCIAL', 'PSICOLOGIA', 'DENTISTA', 'NUTRIÇÃO', 'ENFERMAGEM', 'MEDICINA', 'EDUCAÇÃO', 'GERENTE DE UNIDADE', 'DIRETORES', 'DUAP', 'MONITORAMENTO'],

  // Internos
  '/internos': ['DIRETOR', 'ADMINISTRATIVO', 'PLANTONISTA', 'DISCIPLINA', 'SERVIÇO SOCIAL', 'PSICOLOGIA', 'DENTISTA', 'NUTRIÇÃO', 'ENFERMAGEM', 'MEDICINA', 'EDUCAÇÃO', 'GERENTE DE UNIDADE', 'DIRETORES', 'DUAP', 'MONITORAMENTO', ...EXTERNAL_PROFILE_GROUPS],
  '/internos/:internalId': ['DIRETOR', 'ADMINISTRATIVO', 'PLANTONISTA', 'DISCIPLINA', 'SERVIÇO SOCIAL', 'PSICOLOGIA', 'DENTISTA', 'NUTRIÇÃO', 'ENFERMAGEM', 'MEDICINA', 'EDUCAÇÃO', 'GERENTE DE UNIDADE', 'DIRETORES', 'DUAP', 'MONITORAMENTO'],
  // Tela de detalhes somente leitura para perfis externos
  '/internos/detalhes-externo/:internalId': EXTERNAL_PROFILE_GROUPS,
  '/transferencia-cela': ['PLANTONISTA', 'ADMINISTRATIVO', 'DISCIPLINA'],
  '/transferencia-unidade': ['ADMINISTRATIVO', 'DISCIPLINA', 'GERENTE DE UNIDADE', 'DIRETORES', 'DUAP', 'MONITORAMENTO'],
  '/liberacao': ['PLANTONISTA', 'ADMINISTRATIVO', 'DISCIPLINA', 'MONITORAMENTO'],
  '/liberacao/:internalId': ['PLANTONISTA', 'ADMINISTRATIVO', 'DISCIPLINA', 'MONITORAMENTO'],

  // Unidades
  '/unidades': ['DIRETOR', 'ADMINISTRATIVO'],
  // A rota '/unidades/:unit' será gerada dinamicamente, pode tratar no RestrictedRoute para aceitar rotas que comecem com /unidades
  '/unidades/:unit': ['DIRETOR', 'ADMINISTRATIVO'],

  // Humanização gerál
  '/servico-social': ['SERVIÇO SOCIAL'],
  '/psicologia': ['PSICOLOGIA', 'ENFERMAGEM', 'MEDICINA'],
  '/odontologia': ['DENTISTA', 'ENFERMAGEM', 'MEDICINA'],
  '/nutricao': ['NUTRIÇÃO', 'ENFERMAGEM', 'MEDICINA'],
  '/trabalho': ['DISCIPLINA', 'SERVIÇO SOCIAL', 'ADMINISTRATIVO'],
  
  // Saúde/Enfermagem
  '/admissao': ['ENFERMAGEM', 'MEDICINA'],
  '/evolucao': ['ENFERMAGEM', 'MEDICINA'],
  '/demanda-espontanea': ['ENFERMAGEM', 'MEDICINA'],
  '/doencas-cronicas': ['ENFERMAGEM', 'MEDICINA'],
  '/tuberculose': ['ENFERMAGEM', 'MEDICINA'],
  '/hanseniase': ['ENFERMAGEM', 'MEDICINA'],
  '/testes': ['ENFERMAGEM', 'MEDICINA'],
  '/saude-administrativo': ['ADMINISTRATIVO', 'ENFERMAGEM', 'MEDICINA'],
  '/assistencia-farmaceutica': ['ADMINISTRATIVO', 'ENFERMAGEM', 'MEDICINA'],
  '/encaminhamento': ['ENFERMAGEM', 'MEDICINA'],
  '/vacinacao': ['ENFERMAGEM', 'MEDICINA'],
  '/medicacao': ['ENFERMAGEM', 'MEDICINA'],
  '/consulta-interna': ['ENFERMAGEM', 'MEDICINA'],
  '/exames': ['ENFERMAGEM', 'MEDICINA'],

  // Educação
  '/cadastros-edu': ['EDUCAÇÃO'],
  '/educacao': ['EDUCAÇÃO'],
  '/escolas': ['EDUCAÇÃO'],
  '/professores': ['EDUCAÇÃO'],
  '/alunos': ['EDUCAÇÃO'],
  '/cursos': ['EDUCAÇÃO'],
  '/disciplinas': ['EDUCAÇÃO'],
  '/turmas': ['EDUCAÇÃO'],
  '/painel-seduc': ['EDUCAÇÃO'],
  '/acervo-educacao': ['EDUCAÇÃO'],

  // Movimentação
  '/movimentacao': ['ADMINISTRATIVO', 'DISCIPLINA', 'PLANTONISTA', 'GERENTE DE UNIDADE', 'DIRETORES', 'DUAP', 'SERVIÇO SOCIAL'],
  '/cadastro-movimentacao': ['ADMINISTRATIVO', 'DISCIPLINA', 'PLANTONISTA'],
  '/sacolao': ['ADMINISTRATIVO', 'DISCIPLINA', 'PLANTONISTA'],

  //  visitante
  '/cadastro-visitante-sistema': ['SERVIÇO SOCIAL', 'ADMINISTRATIVO', 'DISCIPLINA'],
  '/visitantes': ['SERVIÇO SOCIAL', 'ADMINISTRATIVO', 'DISCIPLINA', 'PLANTONISTA', 'GERENTE DE UNIDADE', 'DIRETORES'],
  // Polícia (perfil externo) também consulta Todos Visitantes e os detalhes, só leitura — ver o modo
  // somente leitura (isUnitManagerViewOnly) em listVisitantesOutros.js e visitantesDetalhes.js.
  '/outros-visitantes': ['SERVIÇO SOCIAL', 'ADMINISTRATIVO', 'DISCIPLINA', 'PLANTONISTA', 'GERENTE DE UNIDADE', 'DIRETORES', 'DUAP', 'POLÍCIA MILITAR / POLÍCIA CIVIL'],
  '/detalhes-visitantes/:visitorId': ['SERVIÇO SOCIAL', 'ADMINISTRATIVO', 'DISCIPLINA', 'PLANTONISTA', 'GERENTE DE UNIDADE', 'DIRETORES', 'DUAP', 'POLÍCIA MILITAR / POLÍCIA CIVIL'],

  // Autorizações
  '/autorizacao-visita-servico-social': ['SERVIÇO SOCIAL'],
  '/autorizacao-visita-disciplina': ['DISCIPLINA'],

  // Agendamento
  '/agendamento': ['DIRETOR', 'ADMINISTRATIVO', 'PLANTONISTA', 'DISCIPLINA', 'SERVIÇO SOCIAL', 'PSICOLOGIA', 'DENTISTA', 'NUTRIÇÃO', 'ENFERMAGEM', 'MEDICINA', 'EDUCAÇÃO', 'ADVOGADO(A)', 'MONITORAMENTO'],

  // Audiências
  '/audiencias': ['DUAP', 'ADMINISTRATIVO', 'GERENTE DE UNIDADE'],

  // Agendamentos de Advogados (DUAP)
  '/agendamentos-advogados': ['DUAP'],

  // Usuário: aberto a qualquer usuário autenticado, independente do grupo.
  '/usuario': [ALL_GROUPS_SENTINEL],
};


const isRouteMatch = (pathPattern, currentPath) => {
  // Trata rotas dinâmicas simples como /unidades/:unitId
  if (pathPattern.includes(':')) {
    // Converte /unidades/:unitId para regex ^/unidades(/[^/]+)?$
    const regex = new RegExp('^' + pathPattern.replace(/:\w+/g, '([^/]+)') + '$');
    return regex.test(currentPath);
  }
  return pathPattern === currentPath;
};

const RestrictedRoute = () => {
  const { userInfo } = useUsers();
  const location = useLocation();
  // Guarda defensiva para evitar erro de leitura de groups.
  const userGroups = Array.isArray(userInfo?.groups) ? userInfo.groups : [];
  const normalizedUserGroups = userGroups.map(normalizeGroupName);

  const currentPath = location.pathname;
  const canAccessRoute = (routePath) => {
    const matched = Object.keys(routeAccessGroups).find((route) => isRouteMatch(route, routePath));
    if (!matched) return false;
    const allowed = routeAccessGroups[matched] || [];
    if (allowed.includes(ALL_GROUPS_SENTINEL)) return true;
    const normalizedAllowed = allowed.map(normalizeGroupName);
    return normalizedUserGroups.some((group) => normalizedAllowed.includes(group));
  };

  // Redirecionamento seguro por perfil para evitar mandar o usuario para rota sem permissao.
  let redirectPath = '/';
  if (normalizedUserGroups.includes(normalizeGroupName('ADVOGADO(A)'))) {
    redirectPath = '/advogado';
  } else if (EXTERNAL_PROFILE_GROUPS.some((group) => normalizedUserGroups.includes(normalizeGroupName(group)))) {
    redirectPath = '/internos';
  } else if (canAccessRoute('/usuario')) {
    redirectPath = '/usuario';
  } else if (!canAccessRoute('/')) {
    redirectPath = '/login';
  }

  // Busca a rota correspondente no objeto routeAccessGroups
  const matchedRoute = Object.keys(routeAccessGroups).find((route) =>
    isRouteMatch(route, currentPath)
  );

  // Se não houver regra para a rota, redireciona para uma página segura do perfil.
  if (!matchedRoute) {
    return <Navigate to={redirectPath} replace />;
  }

  const allowedGroups = routeAccessGroups[matchedRoute] || [];
  const normalizedAllowedGroups = allowedGroups.map(normalizeGroupName);

  // Verifica se usuário tem pelo menos um grupo permitido (ou se a rota é aberta a todos)
  const hasAccess = allowedGroups.includes(ALL_GROUPS_SENTINEL)
    || normalizedUserGroups.some((group) => normalizedAllowedGroups.includes(group));

  if (!hasAccess) {
    return <Navigate to={redirectPath} replace />;
  }

  return <Outlet />;
};

export default RestrictedRoute;
